import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import useDataset from "datasets/hooks/useDataset";
import useDatasetPermission from "datasets/hooks/useDatasetPermission";
import useDatasetDashboards from "datasets/pages/dashboards/hooks/useDatasetDashboards";
import Dashboard from "datasets/pages/dashboards/types/interfaces/dashboard";
import Dataset from "datasets/types/interfaces/dataset";
import { format } from "date-fns";
import { Pencil, Plus, Trash2 } from "lucide-react";
import yn from "yn";
import SigmaEmbed from "~/components/SigmaEmbed";
import { Alert, AlertDescription } from "~/components/ui/alert";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import useToast from "~/components/ui/toast";
import { cn } from "~/lib/utils";
import { V1DashboardResult } from "~/types/interfaces/catalogDashboards";
import { apiMutate } from "~/utils/apiClient";
import { trackEvent } from "~/utils/monitoring";

const { VITE_CATALOG_API, VITE_SIGMA_URL, VITE_SIGMA_SELF_SERVICE_ENABLED } = import.meta.env;

const formatDate = (timestamp: string | number) => format(new Date(timestamp), "MMM dd, yyyy");

interface DashboardModalProps {
  report?: Dashboard;
  onClose: () => void;
  onSaved: () => void;
}

const DashboardModal = ({ report, onClose, onSaved }: DashboardModalProps) => {
  const { datasetId } = useParams();
  const { mutate: mutateDataset } = useDataset();
  const { mutate: mutateDashboards } = useDatasetDashboards();
  const { showSuccess } = useToast();
  const sigmaSelfServeEnabled = yn(VITE_SIGMA_SELF_SERVICE_ENABLED);
  const isEditing = !!report;

  const [displayName, setDisplayName] = useState(report?.displayName ?? "");
  const [description, setDescription] = useState(report?.description ?? "");
  const [sourceUrl, setSourceUrl] = useState(report?.sourceUrl ?? "");
  const [urlError, setUrlError] = useState("");
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const validateUrl = (value: string): boolean => {
    if (!value && (sigmaSelfServeEnabled || isEditing)) {
      return true;
    }
    try {
      const url = new URL(value);
      const valid = url.origin === VITE_SIGMA_URL && url.pathname.startsWith("/manifold/workbook/");
      if (!valid) setUrlError("Invalid Sigma workbook URL");
      return valid;
    } catch {
      setUrlError("Invalid URL");
      return false;
    }
  };

  const refreshData = async () => Promise.all([mutateDataset(), mutateDashboards()]);

  const handleCreate = async () => {
    if (!sigmaSelfServeEnabled && !validateUrl(sourceUrl)) {
      return;
    }
    if (sourceUrl && !validateUrl(sourceUrl)) {
      return;
    }

    const created = await apiMutate<V1DashboardResult>(
      `${VITE_CATALOG_API}/v1/datasets/${datasetId}/dashboards`,
      "POST",
      {
        display_name: displayName.trim(),
        description: description || undefined,
        source_url: sourceUrl || undefined,
      }
    );

    trackEvent("dataset_dashboard_created", {
      dashboard_id: created.id,
      dataset_id: datasetId,
    });
  };

  const handleUpdate = async () => {
    const body: Record<string, string> = {};
    if (displayName.trim() !== report!.displayName) body.displayName = displayName.trim();
    if (description !== report!.description) body.description = description;
    if (sourceUrl !== report!.sourceUrl) {
      if (sourceUrl && !validateUrl(sourceUrl)) {
        return;
      }
      body.sourceUrl = sourceUrl;
    }
    if (Object.keys(body).length === 0) {
      return;
    }

    await apiMutate(
      `${VITE_CATALOG_API}/datasets/${datasetId}/reports/${report!.id}`,
      "PATCH",
      body
    );

    trackEvent("dataset_dashboard_edited", {
      dashboard_id: report!.id,
      dataset_id: datasetId,
    });
  };

  const handleSubmit = async () => {
    if (!displayName.trim()) {
      return;
    }
    setSaving(true);
    setFormError("");
    try {
      if (isEditing) {
        await handleUpdate();
      } else {
        await handleCreate();
      }
      await refreshData();
      showSuccess(isEditing ? "Dashboard saved" : "Dashboard added");
      onSaved();
      onClose();
    } catch (e: any) {
      try {
        const parsed = JSON.parse(e.message);
        setFormError(parsed.detail ?? `Failed to ${isEditing ? "update" : "add"} dashboard`);
      } catch {
        setFormError(e.message ?? `Failed to ${isEditing ? "update" : "add"} dashboard`);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit" : "New"} Dashboard</DialogTitle>
        </DialogHeader>
        {formError && (
          <Alert variant="destructive">
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="dashboard-name" className="gap-0">
              Name<span className="text-destructive">*</span>
            </Label>
            <Input
              id="dashboard-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter dashboard name"
              autoFocus
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="dashboard-description">Description</Label>
            <Textarea
              id="dashboard-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter dashboard description"
              rows={3}
            />
          </div>
          {!isEditing && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="dashboard-url" className="gap-0">
                Source URL
                {!sigmaSelfServeEnabled && <span className="text-destructive">*</span>}
              </Label>
              <Input
                id="dashboard-url"
                value={sourceUrl}
                onChange={(e) => {
                  setSourceUrl(e.target.value);
                  setUrlError("");
                }}
                placeholder="Enter Sigma workbook URL"
                className={cn(urlError && "border-destructive")}
              />
              {urlError ? (
                <p className="text-xs text-destructive">{urlError}</p>
              ) : (
                sigmaSelfServeEnabled && (
                  <p className="text-xs text-muted-foreground">
                    If left empty, a new blank dashboard will be created for you
                  </p>
                )
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!displayName.trim() || saving}>
            {saving ? (isEditing ? "Saving…" : "Adding…") : isEditing ? "Save" : "Add"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface ReportCardProps {
  report: Dashboard;
  datasetId: string;
  canEdit: boolean;
  canRemove: boolean;
  onEdit: (report: Dashboard) => void;
  onRemove: (report: Dashboard) => void;
  // Opens the dashboard in a full-screen dialog instead of in-app navigation —
  // used when embedding this tab in the session context sidebar, which
  // shouldn't navigate the whole app away from the project page.
  onOpenDialog?: (report: Dashboard) => void;
}

const ReportRow = ({
  report,
  datasetId,
  canEdit,
  canRemove,
  onEdit,
  onRemove,
  onOpenDialog,
}: ReportCardProps) => {
  const href = `/nova/datasets/${datasetId}/dashboards/${report.id}`;
  const rowClassName = cn(
    "flex items-center gap-3 px-4 py-3",
    "hover:bg-accent/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
    canEdit && "pr-20"
  );
  const rowContent = (
    <>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium">{report.displayName}</p>
        {report.description && (
          <p className="mt-0.5 text-xs text-muted-foreground">{report.description}</p>
        )}
      </div>
      <p className="shrink-0 text-xs text-muted-foreground">
        Updated {formatDate(report.updatedAt)}
      </p>
    </>
  );

  return (
    <li className="group relative first:rounded-t-lg last:rounded-b-lg">
      {onOpenDialog ? (
        <button
          type="button"
          onClick={() => onOpenDialog(report)}
          className={cn(rowClassName, "w-full cursor-pointer text-left")}
        >
          {rowContent}
        </button>
      ) : (
        <Link to={href} className={rowClassName}>
          {rowContent}
        </Link>
      )}
      {canEdit && (
        <div
          className={cn(
            "absolute right-2 top-1/2 -translate-y-1/2",
            "flex shrink-0 items-center gap-1",
            "opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100"
          )}
        >
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label={`Edit ${report.displayName}`}
            onClick={(e) => {
              e.preventDefault();
              onEdit(report);
            }}
          >
            <Pencil className="size-4" />
          </Button>
          {canRemove && (
            <Button
              variant="ghost"
              size="icon"
              className="size-7 text-muted-foreground hover:text-destructive"
              aria-label={`Remove ${report.displayName}`}
              onClick={(e) => {
                e.preventDefault();
                onRemove(report);
              }}
            >
              <Trash2 className="size-4" />
            </Button>
          )}
        </div>
      )}
    </li>
  );
};

interface DatasetDashboardsProps {
  // Overrides the route-fetched dataset — used when this tab is rendered
  // outside the DatasetDetail route tree (the project context sidebar).
  dataset?: Dataset;
  // True when this tab is rendered inside the session context sidebar,
  // which only ever shows a read-only view (no edit affordances, no tab
  // title — the entity name is already shown in the sidebar header).
  sessionSidebarView?: boolean;
}

export const DatasetDashboards = ({
  dataset: datasetProp,
  sessionSidebarView = false,
}: DatasetDashboardsProps = {}) => {
  const { data: fetchedDataset, mutate: mutateDataset } = useDataset(datasetProp?.id);
  const dataset = datasetProp ?? fetchedDataset;
  const { data: reports, isLoading, mutate: mutateDashboards } = useDatasetDashboards(dataset?.id);
  const canCreateGrant = useDatasetPermission("create:catalog-report", dataset?.id);
  const canEditGrant = useDatasetPermission("edit:catalog-report", dataset?.id);
  const canCreate = !sessionSidebarView && canCreateGrant;
  const canEdit = !sessionSidebarView && canEditGrant;
  const canRemove = canEdit;
  const { showError, showSuccess } = useToast();

  const [modalTarget, setModalTarget] = useState<Dashboard | "new" | null>(null);
  const [removeTarget, setRemoveTarget] = useState<Dashboard | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [dialogReport, setDialogReport] = useState<Dashboard | null>(null);

  const handleRemoveConfirm = async () => {
    if (!removeTarget || !reports || !dataset) {
      return;
    }
    setIsRemoving(true);
    try {
      const filtered = reports
        .filter((report) => report.fqn !== removeTarget.fqn)
        .map((report) => report.fqn);
      await apiMutate(`${VITE_CATALOG_API}/datasets/${dataset.id}`, "PATCH", {
        reports: filtered,
      });
      trackEvent("dataset_dashboard_removed", {
        dashboard_id: removeTarget.id,
      });
      await Promise.all([mutateDataset(), mutateDashboards()]);
      showSuccess("Dashboard removed");
      setRemoveTarget(null);
    } catch (e: any) {
      showError(e.message ?? "Failed to remove dashboard");
    } finally {
      setIsRemoving(false);
    }
  };

  const isEmpty = !isLoading && (!reports || reports.length === 0);

  return (
    <>
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
        {!sessionSidebarView && (
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-semibold">Dashboards</h1>
            {canCreate && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setModalTarget("new")}
                className="text-primary hover:text-primary/80"
              >
                <Plus className="size-4" />
                Add Dashboard
              </Button>
            )}
          </div>
        )}

        {isLoading ? (
          <div role="status" aria-label="Loading dashboards" className="flex flex-col gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-14 animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        ) : isEmpty ? (
          <div className="rounded-lg border border-dashed border-border px-4 py-10 text-center">
            <p className="text-sm text-muted-foreground">No dashboards yet.</p>
          </div>
        ) : (
          <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
            {(reports ?? []).map((report) => (
              <ReportRow
                key={report.id}
                report={report}
                datasetId={dataset?.id ?? ""}
                canEdit={canEdit}
                canRemove={canRemove}
                onEdit={setModalTarget}
                onRemove={setRemoveTarget}
                onOpenDialog={sessionSidebarView ? setDialogReport : undefined}
              />
            ))}
          </ul>
        )}
      </div>

      {modalTarget !== null && (
        <DashboardModal
          report={modalTarget === "new" ? undefined : modalTarget}
          onClose={() => setModalTarget(null)}
          onSaved={() => setModalTarget(null)}
        />
      )}

      {sessionSidebarView && dialogReport && (
        <SigmaEmbed
          title={dialogReport.displayName}
          datasetId={dataset?.id}
          reportFqn={dialogReport.fqn}
          onClose={() => setDialogReport(null)}
        />
      )}

      <AlertDialog
        open={!!removeTarget}
        onOpenChange={(open) => !open && !isRemoving && setRemoveTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove &ldquo;{removeTarget?.displayName}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the dashboard from this dataset.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRemoving}>Cancel</AlertDialogCancel>
            <Button variant="destructive" onClick={handleRemoveConfirm} disabled={isRemoving}>
              {isRemoving ? "Removing…" : "Remove"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
